import type { ImgHTMLAttributes } from 'react';
import {
	type ImageRoughSizeOptions,
	buildPictureSources,
	isOptimizableUrl,
} from './optimize-image-size';
import { getConfig } from '@fshr/shared-react';
import { ImagePrimitive } from '@fshr/shared/types';
import { imgOpt } from '@fshr/shared';

type ImageProps = ImgHTMLAttributes<HTMLImageElement> & {
	/**
	 * This is meant as a rough measure of how much of the
	 * container this image will take up. The loose correlation would be
	 *  s: < ~1/3 of the container
	 *  m: > ~1/3 and < ~2/3
	 *  l: > ~2/3
	 *
	 * In general you shouldn't have to change this from one breakpoint to another as the component itself
	 * will adjust the actual image size needed based on the breakpoint. In edge cases it can be done though.
	 * ex: on Mobile the image is displayed as a thumbnail (s) but on desktop the same image is displayed full screen (l)
	 */
	roughSize: ImageRoughSizeOptions;

	staticImage?: boolean;

	imagePrimitive?: ImagePrimitive;
};

export default function Image({
	roughSize,
	src,
	staticImage = false,
	imagePrimitive,
	alt,
	...imgProps
}: ImageProps) {
	const { NEXT_PUBLIC_IMAGE_BASE_URL } = getConfig();

	// imported svgs come through with a src as an obj
	let resolvedSrc: string = (src as any)?.src || src;

	if (imagePrimitive?.defaultImage?.path) {
		resolvedSrc = imgOpt({
			path: imagePrimitive.defaultImage.path,
			cropping: imagePrimitive.defaultImage.cropping,
		});
	}

	if (typeof resolvedSrc !== 'string') {
		console.error('unexpected img src', resolvedSrc);
		return null;
	}

	const prefixedSrc =
		resolvedSrc.startsWith('/') && !staticImage
			? `${NEXT_PUBLIC_IMAGE_BASE_URL || ''}${resolvedSrc}`
			: resolvedSrc;

	const resolvedAlt = imagePrimitive?.alt || alt;

	// Only img-opt URLs can be resized; anything else (plain assets, imported svgs) is rendered
	// as-is. A responsive `<picture>` is fully declarative, so the browser resolves the right
	// rendition for the viewport and pixel density with no re-render as the container changes.
	if (!isOptimizableUrl(prefixedSrc)) {
		return <img src={prefixedSrc} alt={resolvedAlt} {...imgProps} />;
	}

	// a specific width was provided as a number: the rendered size is known, so skip the
	// media-gated sources and just offer a density srcset.
	const exactImageWidth = typeof imgProps.width === 'number' ? imgProps.width : undefined;

	let sources: ReturnType<typeof buildPictureSources>['sources'] = [];
	let fallbackSrc = prefixedSrc;
	let fallbackSrcSet = '';
	try {
		({ sources, fallbackSrc, fallbackSrcSet } = buildPictureSources({
			url: prefixedSrc,
			roughSize,
			exactImageWidth,
		}));
	} catch (e) {
		// something unexpected happened, fall back to the un-optimized src
		console.error('failed to build responsive image sources', e);
		return <img src={prefixedSrc} alt={resolvedAlt} {...imgProps} />;
	}

	return (
		// `display: contents` keeps the <picture> out of layout so consumer classes on the <img>
		// (e.g. `h-full`, `absolute inset-0`) still resolve against the original parent.
		<picture style={{ display: 'contents' }}>
			{sources.map(source => (
				<source key={source.media} media={source.media} srcSet={source.srcSet} />
			))}
			<img src={fallbackSrc} srcSet={fallbackSrcSet || undefined} alt={resolvedAlt} {...imgProps} />
		</picture>
	);
}
